import { Component, DestroyRef, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { IIntegration } from "@shared/components/interfaces/integration.interface";
import { AppConstants } from "@core/constants";
import { MatLegacySnackBar as MatSnackBar } from "@angular/material/legacy-snack-bar";
import { from, Observable } from 'rxjs';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ApiInfoDto } from '@core/services/manage-organizations/manage-organization-dto';
import { ActivatedRoute } from '@angular/router';
import { ApiBaseService } from '@core/services/api-base.service';


const EXTERNAL_API_NAMES: Record<string, string> = {
  share_data: "Integration with AZUM",
  get_athlete_test_v2: "Athlete KPIs"
}

const EXTERNAL_API_HELPDESK: Record<string, string> = {
  share_data: "https://helpcenter.inscyd.com/en/articles/11462637-azum-integration",
  get_athlete_test_v2: "https://helpcenter.inscyd.com/en/articles/11462689-athlete-kpis-integrations"
}

@Component({
  selector: 'app-integration-item',
  templateUrl: './integration-item.component.html',
  styleUrls: ['./integration-item.component.scss']
})
export class IntegrationItemComponent implements OnInit, OnChanges {
  private snackBar: MatSnackBar = inject(MatSnackBar);
  private destroyRef: DestroyRef = inject(DestroyRef);

  @Input() config: IIntegration;
  @Input() apiInfo: ApiInfoDto[];

  public apisForm!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private apiService: ApiBaseService,
    private activatedRoute: ActivatedRoute,
  ) { }

  ngOnInit(): void {
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.populateForm();
  }



  private initForm() {
    const formGroupData: Record<string, FormControl> = {};
    this.formKeys().forEach(key => { formGroupData[key] = new FormControl<boolean>(false) });
    this.apisForm = this.fb.group(formGroupData);
  }

  private populateForm() {
    const organization = this.activatedRoute.snapshot.data.data;
    if (!organization) {
      return 
    }
  
    const apis: ApiInfoDto[] = (organization.api ?? []).map((api: ApiInfoDto) => api.id);
    if (!apis) {
      return
    }

    const formValues: Record<string, boolean> = {};
    const formKeys = this.formKeys();
    formKeys.forEach(key => { formValues[key] = false });
    (organization.api ?? []).forEach((api: ApiInfoDto) => {
      if (api.alias) {
        const aliasFormKey = this.getFormKeyFromAlias(api.alias);
        if (formKeys.includes(aliasFormKey)) {
          formValues[this.getFormKeyFromAlias(api.alias)] = true;
        }
      }
    });

    if (this.apisForm) {
      this.apisForm.setValue(formValues);
    }
  }

  public formKeys(): string[] {
    return AppConstants.VISIBLE_API_INFO_ALIASES.map(alias => this.getFormKeyFromAlias(alias));
  }

  public getNameFromKey(key: string) {
    if (key in EXTERNAL_API_NAMES) {
      return EXTERNAL_API_NAMES[key]
    }
    const alias = this.getAliasFromKey(key);
    return this.apiInfo.find(api => api.alias != null && api.alias == alias)?.name;
  }

  public getHelpDeskFromKey(key: string): string {
    return key in EXTERNAL_API_HELPDESK ? EXTERNAL_API_HELPDESK[key] : ""
  }

  public save() {
    const formData = this.apisForm.value;
    const apiIDs = this.apiInfo.filter(api => {
      if (api.alias == null) {
        return false
      }
      return !!formData[this.getFormKeyFromAlias(api.alias)];
    }).map(api => api.id);

    this.apiService.postJson(AppConstants.API.INTEGRATIONS.LINKS.APIS, { "apis": apiIDs }).subscribe({
      next: () => {
        this.snackBar.open("Organization API links updated", 'OK', AppConstants.TOAST_CONFIG.SUCCESS);
      },
      error: (err) => {
        this.snackBar.open(err, 'OK', AppConstants.TOAST_CONFIG.ERROR);
      }
    })
  }

  private getAliasFromKey(key: string): string {
    return key.replace(/_/g, "-");
  }

  private getFormKeyFromAlias(key: string): string {
    return key.replace(/-/g, "_");
  }
}
